import React, { useState, useCallback } from "react";
import { Loader2, Check, X, Repeat, Plus, ShieldCheck, Trash2, PackageOpen, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ScanBar, PickPill, Card, EmptyState } from "./fxui";
import { QrLabelButton } from "./QrLabel";

// Single scan-driven flow: open an order -> buy/pick every item -> pack -> done.
export default function FxPrepare() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ptype, setPtype] = useState("box");
  const [weight, setWeight] = useState("");

  const loadOrder = useCallback(async (onum) => {
    const { data } = await api.get(`/fx/orders/${onum}`);
    setOrder(data);
    return data;
  }, []);

  const open = async (code) => {
    setLoading(true);
    try {
      let c = code.trim();
      let markKey = null;
      if (c.includes("~")) { const [onum, key] = c.split("~"); c = onum; markKey = key; }
      const { data: scan } = await api.get("/fx/scan", { params: { code: c } });
      const onum = scan.order_number || c;
      const data = await loadOrder(onum);
      if (markKey && data.items.some((i) => i.key === markKey)) { await mark(onum, markKey, "picked", { qty_found: 1 }); toast.success("Marked bought"); }
    } catch { toast.error("Order not found"); setOrder(null); }
    finally { setLoading(false); }
  };

  const reload = () => order && loadOrder(order.order_number);
  const mark = async (onum, key, status, extra = {}) => {
    try { await api.post(`/fx/pick/${onum}/item`, { key, pick_status: status, ...extra }); await loadOrder(onum); }
    catch { toast.error("Could not update item"); }
  };
  const buy = (key, qty) => mark(order.order_number, key, "picked", { qty_found: qty });
  const substitute = (key) => { const n = window.prompt("What did you buy instead?"); if (n) mark(order.order_number, key, "substitute", { substitute_note: n }); };
  const unavailable = (key) => mark(order.order_number, key, "not_found", { exception_reason: "Not available" });

  const itemsReady = async () => {
    try { await api.post(`/fx/pick/${order.order_number}/complete`); toast.success("Items ready — now pack the order"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Some items still to handle"); }
  };
  const addPackage = async () => {
    try { await api.post("/fx/packages", { order_number: order.order_number, package_type: ptype, weight_kg: weight ? Number(weight) : null }); toast.success("Package created"); setWeight(""); reload(); }
    catch { toast.error("Could not create package"); }
  };
  const qc = async (pn) => { await api.put(`/fx/packages/${pn}/qc`, { qc_status: "passed" }); toast.success("QC passed — order packed"); reload(); };
  const delPkg = async (pn) => { await api.delete(`/fx/packages/${pn}`); reload(); };

  const allHandled = order && order.items.length > 0 && order.items.every((i) => i.pick_status !== "pending");
  const handled = order ? order.items.filter((i) => i.pick_status !== "pending").length : 0;
  const isPacked = order && order.public_step >= 2;
  const pickComplete = order && (order.internal_status || "").toLowerCase().includes("pick") ? true : (order && order.packages && order.packages.length > 0);

  return (
    <div data-testid="fx-prepare">
      <ScanBar onSubmit={open} placeholder="Scan or type an order to prepare…" testid="fx-prepare-scan" />
      {loading && <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>}
      {!loading && !order && <EmptyState>Scan an order QR (or type its number) to buy items and pack it — all on one page.</EmptyState>}

      {order && !loading && (
        <>
          <Card className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[160px]">
              <p className="font-display text-2xl text-[#0D530E]">{order.order_number}</p>
              <p className="text-sm text-[#5C5C5C]">{order.customer.name} · {order.customer.city}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl text-[#306D29]">{handled}/{order.items.length}</p>
              <p className="text-xs text-[#5C5C5C]">items handled</p>
            </div>
            <QrLabelButton code={order.order_number} title={order.customer.name} subtitle={order.customer.city} testid="fx-prepare-order-label" label="Order label" />
          </Card>

          {/* STEP 1 — Buy / pick items */}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-[#306D29] text-white text-xs font-bold flex items-center justify-center">1</span>
            <p className="font-semibold text-[#0D530E]">Buy the items</p>
          </div>
          <div className="space-y-2 mb-6">
            {order.items.map((it) => (
              <div key={it.key} className="bg-white border border-black/5 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`fx-prep-item-${it.key}`}>
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-medium">{it.name} <span className="text-xs text-[#5C5C5C]">× {it.quantity}</span></p>
                  <p className="text-xs text-[#5C5C5C]">{it.source_store || "store not set"}{it.substitute_note ? ` · sub: ${it.substitute_note}` : ""}</p>
                </div>
                <PickPill status={it.pick_status} />
                <div className="flex gap-2">
                  <button onClick={() => buy(it.key, it.quantity)} data-testid={`fx-prep-buy-${it.key}`}
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full ${it.pick_status === "picked" ? "bg-[#306D29] text-white" : "bg-black/5 text-[#306D29] hover:bg-[#306D29]/10"}`}><Check size={13} /> Bought</button>
                  <button onClick={() => substitute(it.key)} data-testid={`fx-prep-sub-${it.key}`}
                    className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-black/5 text-[#2563EB] hover:bg-[#2563EB]/10"><Repeat size={13} /> Sub</button>
                  <button onClick={() => unavailable(it.key)} data-testid={`fx-prep-na-${it.key}`}
                    className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-black/5 text-[#B23B3B] hover:bg-[#B23B3B]/10"><X size={13} /> N/A</button>
                </div>
              </div>
            ))}
          </div>

          {/* STEP 2 — Pack */}
          <div className="flex items-center gap-2 mb-3">
            <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${allHandled ? "bg-[#306D29] text-white" : "bg-black/10 text-[#5C5C5C]"}`}>2</span>
            <p className={`font-semibold ${allHandled ? "text-[#0D530E]" : "text-[#5C5C5C]"}`}>Pack the order</p>
          </div>

          {!allHandled ? (
            <div className="bg-white border border-dashed border-black/15 rounded-2xl p-6 text-center text-sm text-[#5C5C5C]" data-testid="fx-prepare-pack-locked">
              Handle every item above, then packing unlocks. <span className="text-[#306D29] font-semibold">{order.items.length - handled} left</span>
            </div>
          ) : (
            <>
              {(!order.packages || order.packages.length === 0) && (
                <button onClick={itemsReady} data-testid="fx-prepare-ready"
                  className="w-full mb-4 bg-[#0D530E] hover:bg-[#306D29] text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2" style={{ transition: "background-color 0.2s ease" }}>
                  <CheckCircle2 size={18} /> Items ready — start packing <ArrowRight size={16} />
                </button>
              )}
              <div className="flex gap-2 mb-4">
                <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="fx-prepare-ptype" className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm">
                  <option value="box">Box</option><option value="bag">Bag</option><option value="pallet">Pallet</option>
                </select>
                <input value={weight} onChange={(e) => setWeight(e.target.value)} type="number" placeholder="Weight (kg)" data-testid="fx-prepare-weight" className="flex-1 bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
                <button onClick={addPackage} data-testid="fx-prepare-addpkg" className="bg-[#306D29] text-white px-4 rounded-xl text-sm font-semibold flex items-center gap-1"><Plus size={15} /> Package</button>
              </div>
              <div className="space-y-2">
                {(order.packages || []).map((p) => (
                  <div key={p.package_number} className="bg-white border border-black/5 rounded-xl px-4 py-3 flex items-center gap-3">
                    <PackageOpen size={16} className="text-[#306D29]" />
                    <div className="flex-1"><p className="text-sm font-medium">{p.package_number}</p><p className="text-xs text-[#5C5C5C]">{p.package_type}{p.weight_kg ? ` · ${p.weight_kg}kg` : ""}</p></div>
                    {p.qc_status === "passed" ? <span className="text-[#306D29] text-xs font-semibold flex items-center gap-1"><ShieldCheck size={14} /> QC passed</span>
                      : <button onClick={() => qc(p.package_number)} data-testid={`fx-prepare-qc-${p.package_number}`} className="text-xs font-semibold bg-[#306D29] text-white px-3 py-1.5 rounded-full">Pass QC</button>}
                    <QrLabelButton code={p.package_number} title={order.order_number} subtitle={order.customer.name} testid={`fx-prepare-pkglabel-${p.package_number}`} label="Label" />
                    <button onClick={() => delPkg(p.package_number)} className="text-[#B23B3B] w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#B23B3B]/10"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              {isPacked && (
                <div className="mt-5 bg-[#306D29]/10 border border-[#306D29]/25 text-[#306D29] rounded-2xl px-5 py-4 flex items-center gap-2 font-semibold" data-testid="fx-prepare-done">
                  <CheckCircle2 size={18} /> Order packed and ready for a container.
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
